import React, { useState, useRef, useEffect } from "react";
import { Icons } from "../../constants/icon-library";
import { getIcon } from "../../constants/constants";
import { createPortal } from "react-dom";
import MultiSelect from "../../components/common/MultiSelect";
import { getParticipantSecondary } from "../Meetings/Meetings.utils";

export const PlatformDropdown = ({
  settings = [],
  action,
  onSettingChange,
}) => {
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const ref = useRef(null);

  const selectedSetting = settings.find(
    (setting) => setting.uuid === action?.settingId,
  );

  useEffect(() => {
    const handler = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 200;
      const openUpward = spaceBelow < dropdownHeight;
      setDropdownPos({
        top: openUpward
          ? rect.top + window.scrollY - dropdownHeight - 4
          : rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
    setOpen((prev) => !prev);
  };

  const selectedPlatformType = selectedSetting?.assistantId?.platform?.type;
  const icon =
    getIcon(selectedPlatformType) ||
    selectedSetting?.assistantId?.platform?.iconUrl;
  const displayName = selectedSetting
    ? selectedSetting.assistantName || selectedSetting.assistantId?.name || "—"
    : "—";

  const dropdownMenu =
    open &&
    createPortal(
      <div
        ref={ref}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: dropdownPos.top,
          left: dropdownPos.left,
          minWidth: "200px",
          zIndex: 9999,
          maxHeight: "200px",
          overflowY: "auto",
          display: "block",
        }}
      >
        {settings.length === 0 ? (
          <span className="dropdown-item text-muted small">
            No settings available
          </span>
        ) : (
          settings.map((setting) => {
            const label =
              setting.assistantName || setting.assistantId?.name || "Unknown";
            const settingIcon =
              getIcon(setting.assistantId?.platform?.type) ||
              setting?.assistantId?.platform?.iconUrl;
            const isSelected = setting.uuid === action?.settingId;

            return (
              <button
                key={setting.uuid}
                className={`dropdown-item d-flex align-items-center gap-2 ${
                  isSelected ? "active" : ""
                }`}
                onClick={() => {
                  onSettingChange(setting.uuid);
                  setOpen(false);
                }}
              >
                {settingIcon && (
                  <img
                    src={settingIcon}
                    alt={label}
                    width={16}
                    height={16}
                    className="object-fit-contain"
                  />
                )}
                <span className="small" style={{ color: "currentColor" }}>
                  {label}
                </span>
              </button>
            );
          })
        )}
      </div>,
      document.body,
    );

  return (
    <>
      <span
        ref={triggerRef}
        className="d-inline-flex align-items-center gap-2"
        style={{ cursor: "pointer" }}
        title={displayName}
        onClick={handleToggle}
      >
        {icon && (
          <img
            src={icon}
            alt={displayName}
            width={18}
            height={18}
            className="object-fit-contain"
          />
        )}
        <span className="small text-muted">{displayName}</span>
        <i
          className={`wb ${Icons("arrow-down")} small`}
          style={{ fontSize: "10px", color: "currentColor", opacity: 0.5 }}
        />
      </span>

      {dropdownMenu}
    </>
  );
};

export const MultiSelectAssigneeDropdown = ({
  participants = [],
  action,
  onAssigneeChange,
}) => {
  const options = participants.map((p) => ({
    value: p.email || p.username,
    label: p.name,
    secondary: getParticipantSecondary(p),
  }));


  return (
    <MultiSelect
      options={options}
      value={
        Array.isArray(action?.email)
          ? action.email
          : action?.email
            ? [action.email]
            : []
      }
      onChange={onAssigneeChange}
      placeholder="Assignee.."
      avatarMode={true}
    />
  );
};

export const AssigneeDropdown = ({
  participants = [],
  action,
  onAssigneeChange,
}) => {
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const triggerRef = useRef(null);
  const ref = useRef(null);

  const currentValue = Array.isArray(action?.email)
    ? action.email[0]
    : action?.email;

  const getIdentifier = (p) => p.email || p.username;

  const selectedUser = participants.find(
    (p) => getIdentifier(p) === currentValue,
  );

  useEffect(() => {
    const handler = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 200;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight;

      setDropdownPos({
        top: openUpward
          ? rect.top + window.scrollY - dropdownHeight - 4
          : rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }

    setOpen((prev) => !prev);
  };

  const dropdownMenu =
    open &&
    createPortal(
      <div
        ref={ref}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: dropdownPos.top,
          left: dropdownPos.left,
          minWidth: "200px",
          zIndex: 9999,
          maxHeight: "200px",
          overflowY: "auto",
        }}
      >
        {participants.map((p) => {
          const identifier = getIdentifier(p);
          const isSelected = currentValue === identifier;

          return (
            <button
              key={p.uuid}
              className={`dropdown-item d-flex align-items-center gap-2 ${
                isSelected ? "active" : ""
              }`}
              onClick={() => {
                onAssigneeChange(identifier);
                setOpen(false);
              }}
            >
              <i className="bi bi-person-circle" />
              <span className="small fw-semibold">
                {p.name}{" "}
                <span className="text-muted">
                  ({getParticipantSecondary(p)})
                </span>
              </span>
            </button>
          );
        })}
      </div>,
      document.body,
    );

  return (
    <>
      <span
        ref={triggerRef}
        className="d-inline-flex align-items-center gap-2"
        style={{ cursor: "pointer", maxWidth: "100px" }}
        onClick={handleToggle}
      >
        <i className="bi bi-person-circle flex-shrink-0" />
        <span
          className="small text-muted"
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {selectedUser
            ? `${selectedUser.name} (${getParticipantSecondary(selectedUser)})`
            : "Unassigned"}
        </span>
        <i
          className={`wb ${Icons("arrow-down")} small flex-shrink-0`}
          style={{ fontSize: "10px", color: "currentColor", opacity: 0.5 }}
        />
      </span>
      {dropdownMenu}
    </>
  );
};

export const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: "Pending", dot: "bg-warning", text: "text-warning" },
    completed: { label: "Completed", dot: "bg-success", text: "text-success" },
    failed: { label: "Failed", dot: "bg-danger", text: "text-danger" },
  };

  const { label, text } = config[status] ?? config.pending;

  return (
    <span className={`d-inline-flex align-items-center gap-1 ${text}`}>
      <span className="small fw-medium">{label}</span>
    </span>
  );
};

export const CustomDateInput = React.forwardRef(
  ({ value, placeholder, onClick, disabled }, ref) => (
    <div
      ref={ref}
      onClick={!disabled ? onClick : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        cursor: disabled ? "not-allowed" : "pointer",
        padding: "4px 8px",
        borderRadius: "6px",
        border: "1px solid #3a3a4a",
        background: "transparent",
        color: value ? "currentColor" : "#999",
        fontSize: "13px",
        whiteSpace: "nowrap",
        minWidth: "110px",
      }}
    >
      <i
        className={`wb ${Icons("calendar")} align-middle`}
        style={{ opacity: 0.5, flexShrink: 0 }}
      />
      <span>{value || placeholder}</span>
    </div>
  ),
);

export const CustomTimeInput = React.forwardRef(
  ({ value, placeholder, onClick, disabled }, ref) => (
    <div
      ref={ref}
      onClick={!disabled ? onClick : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        cursor: disabled ? "not-allowed" : "pointer",
        padding: "4px 8px",
        borderRadius: "6px",
        border: "1px solid #3a3a4a",
        background: "transparent",
        color: value ? "currentColor" : "#999",
        fontSize: "13px",
        whiteSpace: "nowrap",
        minWidth: "90px",
      }}
    >
      <i className="bi bi-clock" style={{ opacity: 0.6 }} />
      <span>{value || placeholder}</span>
    </div>
  ),
);

export const parseTimeString = (timeStr) => {
  if (!timeStr) return null;

  const [h, m] = timeStr.split(":");
  const d = new Date();

  d.setHours(parseInt(h, 10));
  d.setMinutes(parseInt(m, 10));
  d.setSeconds(0);

  return isNaN(d.getTime()) ? null : d;
};

export const formatToHHMM = (date) => {
  if (!date) return "";

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${hours}:${minutes}`;
};

export const TaskOptionsDropdown = ({
  options = [],
  value = "",
  onChange,
  disabled,
  type,
  requireOptionFormat = true,
}) => {
  if (!options.length) return <span className="text-muted">—</span>;

  if (type === "multiselect") {
    let formattedOptions = options;

    if (requireOptionFormat) {
      formattedOptions = options.map((opt) => ({
        value: opt.key,
        label: opt.value,
      }));
    }

    return (
      <MultiSelect
        options={formattedOptions}
        value={value || []}
        onChange={onChange}
        placeholder="Select..."
        avatarMode={true}
        disabled={disabled}
      />
    );
  }

  return (
    <select
      className="form-select form-select-sm"
      style={{
        minWidth: "90px",
        height: "38px",
        fontSize: "12px",
      }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">— Select —</option>

      {options.map((opt) => (
        <option key={opt.key} value={opt.key}>
          {opt.value}
        </option>
      ))}
    </select>
  );
};